import Phaser from 'phaser';
import { CHAPTER_TITLE, GAME_H, GAME_TITLE, GAME_W } from '../config';
import { ctxOf } from '../context';
import { SceneKeys } from './keys';
import type { StoryFlagId } from '@/story/flags';
import { renderTitleIfAvailable } from '../wiring';
import { audio } from '@/audio';

/**
 * Minimal title (integrator shell). The save/UI builder replaces the visuals; the behaviour
 * contract stays: shows 南天传说 / 南洋行, "新的旅程", and "继续旅程" when a save exists.
 */
export class TitleScene extends Phaser.Scene {
  private items: Phaser.GameObjects.Text[] = [];
  private index = 0;
  constructor() { super(SceneKeys.TITLE); }

  create(): void {
    const ctx = ctxOf(this);
    this.cameras.main.setBackgroundColor('#0b0f14');
    // The last map theme must not keep playing on the title screen.
    try { audio().playMusic(null, 800); audio().setAmbience([], 800); } catch { /* audio optional */ }
    this.add.text(GAME_W / 2, 96, GAME_TITLE, { fontFamily: 'serif', fontSize: '40px', color: '#e2c48c' }).setOrigin(0.5);
    this.add.text(GAME_W / 2, 140, `第一章 · ${CHAPTER_TITLE}`, { fontFamily: 'serif', fontSize: '18px', color: '#c9b89a' }).setOrigin(0.5);

    // Verification/debug entry: ?flag=X (and ?weather=Y) jumps straight into the route once.
    if (ctx.url.flag && !this.registry.get('urlFlagConsumed')) {
      this.registry.set('urlFlagConsumed', true);
      if (ctx.url.weather) ctx.weather = ctx.url.weather;
      this.time.delayedCall(60, () => {
        window.__GAME_READY__ = true;
        if (window.__nantian) window.__nantian.ready = true;
        void ctx.jumpTo(ctx.url.flag as StoryFlagId);
      });
    }

    const latest = ctx.saves.latest();
    const entries: { label: string; run: () => void }[] = [];
    if (latest) entries.push({ label: '继续旅程', run: () => void ctx.load(latest.slot) });
    entries.push({ label: '新的旅程', run: () => void ctx.newGame() });

    // The UI builder's title renderer takes over the visuals + menu when available.
    if (renderTitleIfAvailable(this, { entries, onReady: () => this.markReady() })) { this.markReady(); return; }

    entries.forEach((e, i) => {
      const t = this.add.text(GAME_W / 2, 220 + i * 28, e.label, { fontFamily: 'sans-serif', fontSize: '18px', color: '#ffffff' }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      t.on('pointerover', () => this.select(i));
      t.on('pointerdown', () => { this.select(i); e.run(); });
      t.setData('run', e.run);
      this.items.push(t);
    });
    this.select(0);

    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-UP', () => this.select(this.index - 1));
      kb.on('keydown-DOWN', () => this.select(this.index + 1));
      kb.on('keydown-W', () => this.select(this.index - 1));
      kb.on('keydown-S', () => this.select(this.index + 1));
      const confirm = () => (this.items[this.index]?.getData('run') as () => void)?.();
      kb.on('keydown-ENTER', confirm);
      kb.on('keydown-SPACE', confirm);
      kb.on('keydown-Z', confirm);
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { this.items = []; kb?.removeAllListeners(); });

    this.markReady();
    this.add.text(GAME_W - 6, GAME_H - 6, `v${__GAME_VERSION__}`, { fontFamily: 'sans-serif', fontSize: '10px', color: '#5a6473' }).setOrigin(1);
  }

  /** Mark ready for the verification harness once the first frame has rendered. */
  private markReady(): void {
    this.time.delayedCall(50, () => {
      window.__GAME_READY__ = true;
      if (window.__nantian) window.__nantian.ready = true;
    });
  }

  private select(i: number): void {
    if (this.items.length === 0) return;
    this.index = (i + this.items.length) % this.items.length;
    this.items.forEach((t, k) => t.setColor(k === this.index ? '#ffb347' : '#ffffff'));
  }
}
declare const __GAME_VERSION__: string;
